/** Обзор компании: реальные показатели из существующего dashboard API. */
import React, { useEffect, useState } from 'react';
import { Button, Spin, message } from 'antd';
import { ArrowRightOutlined, AppstoreOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { HelpTooltipInline } from './help/HelpButton';
import './dashboard.css';

interface Stats {
  total_orders: number;
  orders_today: number;
  revenue_today: number;
  total_clients: number;
  new_clients_today: number;
  orders_new: number;
  orders_cooking: number;
  orders_delivering: number;
}

const number = (value: number) => new Intl.NumberFormat('ru-RU').format(value);

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/dashboard')
      .then((response) => setStats(response.data))
      .catch((error) => {
        console.error('Dashboard fetch error:', error);
        message.error('Не удалось загрузить статистику');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="dashboard-loading"><Spin size="large" tip="Загрузка обзора..." /></div>;
  }

  if (!stats) {
    return <div className="dashboard-empty"><h1>Обзор пока недоступен</h1><p>Проверьте соединение и обновите страницу.</p><Button onClick={() => window.location.reload()}>Обновить</Button></div>;
  }

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero" aria-labelledby="dashboard-title">
        <div className="dashboard-hero-copy">
          <span className="dashboard-eyebrow">ОБЗОР КОМПАНИИ <HelpTooltipInline sectionId="dashboard" /></span>
          <h1 id="dashboard-title">Сегодня <em>в работе.</em></h1>
          <p>Заказы, клиенты и текущая смена на одном экране.</p>
          <div className="dashboard-hero-actions">
            <Button type="primary" icon={<ShoppingCartOutlined />} onClick={() => navigate('/orders')}>Открыть заказы</Button>
            <Button icon={<AppstoreOutlined />} onClick={() => navigate('/products')}>Перейти к меню</Button>
          </div>
        </div>
        <div className="dashboard-hero-number" aria-label={`Заказов сегодня: ${stats.orders_today}`}>
          <span>ЗАКАЗОВ СЕГОДНЯ</span>
          <strong>{number(stats.orders_today)}</strong>
          <small>Всего за всё время: {number(stats.total_orders)}</small>
        </div>
      </section>

      <section className="dashboard-section" aria-labelledby="dashboard-numbers-title">
        <div className="dashboard-section-heading"><div><span className="dashboard-eyebrow">01 / РЕЗУЛЬТАТ</span><h2 id="dashboard-numbers-title">Главные цифры</h2></div><p>Данные выбранной компании</p></div>
        <div className="dashboard-metrics">
          <article><span>Выручка сегодня</span><strong>{number(stats.revenue_today)} <small>₽</small></strong><p>По данным панели</p></article>
          <article><span>Всего клиентов</span><strong>{number(stats.total_clients)}</strong><p>В базе компании</p></article>
          <article><span>Новых клиентов сегодня</span><strong>{number(stats.new_clients_today)}</strong><p>За текущий день</p></article>
        </div>
      </section>

      <section className="dashboard-section" aria-labelledby="dashboard-active-title">
        <div className="dashboard-section-heading"><div><span className="dashboard-eyebrow">02 / ОПЕРАЦИИ</span><h2 id="dashboard-active-title">Активные заказы</h2></div><Button type="link" onClick={() => navigate('/orders')}>Все заказы <ArrowRightOutlined /></Button></div>
        <div className="dashboard-statuses">
          <article><span className="dashboard-status-dot new" /><div><strong>{number(stats.orders_new)}</strong><span>Новые</span></div></article>
          <article><span className="dashboard-status-dot cooking" /><div><strong>{number(stats.orders_cooking)}</strong><span>Готовятся</span></div></article>
          <article><span className="dashboard-status-dot delivering" /><div><strong>{number(stats.orders_delivering)}</strong><span>Доставляются</span></div></article>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
